import { CircleCheckBig, AlertTriangle, Loader2 } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';

interface StreamTally {
  ok: number;
  remove: number;
  add: number;
  /** REMOVE/ADD rows still missing their (mandatory) comment — NOT registered (OQ-6). */
  incomplete: number;
}

export interface RegisterSummary {
  ssv: StreamTally;
  tsa: StreamTally;
  /** OK + REMOVE + ADD across both streams (complete decisions only). */
  total: number;
  /** Total incomplete (REMOVE/ADD without a comment) across both streams. */
  incomplete: number;
}

interface RegisterModalProps {
  open: boolean;
  onClose: () => void;
  summary: RegisterSummary;
  onConfirm: () => void;
  registering: boolean;
}

/**
 * In-screen "validate & register" confirmation (G2-13a) — the provider's direct
 * alternative to the export→import routine (Gerd round-2). The per-row decisions are
 * already persisted to the working set as they are edited; this is the explicit
 * "I've reviewed my month, register it" affordance + a recap + the OQ-6 guard.
 */
export function RegisterModal({ open, onClose, summary, onConfirm, registering }: RegisterModalProps) {
  return (
    // While the submit is in flight, ignore ESC / click-outside so the modal can't be
    // dismissed mid-POST (the buttons are already disabled) — keeps it open to retry on error.
    <Modal open={open} onClose={registering ? () => {} : onClose} title="Valider et enregistrer mes décisions">
      <div className="space-y-5">
        <p className="text-sm text-slate-500">
          Confirmez vos décisions d'éligibilité pour le mois courant. Elles sont enregistrées et soumises à la
          validation de l'équipe Unifiber. Vous pourrez encore les modifier tant que le mois est ouvert.
        </p>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <StreamCard label="SSV" t={summary.ssv} />
          <StreamCard label="TSA" t={summary.tsa} />
        </div>

        {summary.incomplete > 0 && (
          <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <p>
              <span className="font-semibold">{summary.incomplete} ligne(s) REMOVE/ADD sans commentaire</span> — non
              enregistrée(s) : le commentaire est obligatoire. Renseignez-les puis revalidez.
            </p>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 pt-4">
          <span className="text-xs text-slate-400">
            <span className="font-semibold text-slate-600">{summary.total}</span> décision(s) à enregistrer
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={registering}
              className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-60"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={registering}
              className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {registering ? <Loader2 className="h-4 w-4 animate-spin" /> : <CircleCheckBig className="h-4 w-4" />}
              {registering ? 'Enregistrement…' : 'Confirmer et enregistrer'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

function StreamCard({ label, t }: { label: string; t: StreamTally }) {
  return (
    <div className="rounded-xl border border-slate-200 p-4">
      <div className="text-sm font-bold text-slate-900">Flux {label}</div>
      <div className="mt-2 flex flex-wrap gap-2 text-sm">
        <Pill label="OK" value={t.ok} tone="emerald" />
        <Pill label="REMOVE" value={t.remove} tone="rose" />
        <Pill label="ADD" value={t.add} tone="amber" />
      </div>
    </div>
  );
}

function Pill({ label, value, tone }: { label: string; value: number; tone: 'emerald' | 'rose' | 'amber' }) {
  const tones = {
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    rose: 'border-rose-200 bg-rose-50 text-rose-700',
    amber: 'border-amber-200 bg-amber-50 text-amber-700',
  };
  return <span className={`rounded-lg border px-2.5 py-1 font-semibold ${tones[tone]}`}>{value} {label}</span>;
}
